import { v4 as uuidV4 } from 'uuid';
import { Base64 } from 'js-base64';
import Bowser from 'bowser';

import ResourceProcessorBatch from './lib/ResourceProcessorBatch';
import ResourceProcessor from './lib/ResourceProcessor';
import type {
  ICardData,
  ICommon,
  IMediaResource,
  IPage,
  IPageContent,
  IPageCover,
  IPageEnd,
  IPageGift,
  IPagePrepared,
  IPagePromo,
  IPageRecord,
  IResourcePrepared,
  ISourceResource,
  TAnyObject,
} from './types';

import type {
  IOriginData,
  IOriginDataPageEnd,
  IOriginDataPageGift,
  IOriginDataPageMessage,
  IOriginDataPagePromo,
} from '../../requests/data/types';

export type {
  TAnyObject,
  IPage,
  IMediaResource,
  ISourceResource,
  ICommon,
  ICardData,
  IResourcePrepared,
  IPagePrepared,
  IPageCover,
  IPageContent,
  IPageRecord,
  IPageGift,
  IPagePromo,
  IPageEnd,
};

export {
  ResourceProcessorBatch,
  ResourceProcessor,
};

class DataAdapter {
  private readonly resourceProcessor: ResourceProcessor | ResourceProcessorBatch | undefined;

  constructor(resourceProcessor?: ResourceProcessor | ResourceProcessorBatch | undefined) {
    if (resourceProcessor) {
      this.resourceProcessor = resourceProcessor;
    }
  }

  public async prepare(sourceData: IOriginData): Promise<ICardData> {
    const result: ICardData = {
      common: {
        id: 0,
        type: '',
        uuid: '',
        identity: {
          type: '',
          param: '',
          code: '',
        },
        url: '',
        sender: '',
        isShareableLink: false,
      },
      pages: [],
      resources: [],
    };

    const pages: Promise<IPagePrepared<ICommon | IPageCover | IPageContent | IPageGift | IPagePromo | IPageEnd>>[] = [
      this.prepareCommon(sourceData),
      this.preparePageCover(sourceData),
    ];

    sourceData.pages.forEach((page) => {
      if (page.type === 'message') {
        pages.push(this.preparePageContent(page as IOriginDataPageMessage));
        return;
      }
      if (page.type === 'gift') {
        pages.push(this.preparePageGift(page as IOriginDataPageGift));
        return;
      }
      if (page.type === 'action') {
        pages.push(this.preparePagePromo(page as IOriginDataPagePromo));
      }
      if (page.type === 'end') {
        pages.push(this.preparePageEnd(page as IOriginDataPageEnd));
      }
    });

    const prepared = await Promise.all(pages);

    prepared.forEach((item) => {
      if (!item) {
        return;
      }
      const isCommon = ['b2b', 'b2c'].includes(item.data.type);
      if (isCommon) {
        const { data, resources } = item as IPagePrepared<ICommon>;
        result.common = data;
        result.resources.push(...resources);
        return;
      }
      const { data, resources } = item as IPagePrepared<IPage>;
      if (data.itemsOrder.length === 0) {
        return;
      }
      result.pages.push(data);
      result.resources.push(...resources);
    });

    return result;
  }

  private castResourceType(sourceType: string): string {
    const types = [
      { source: 'video', cast: 'video' },
      { source: 'image', cast: 'image' },
      { source: 'audio', cast: 'audio' },
      { source: 'poster', cast: 'poster' },
    ];
    const found = types.find((item) => item.source === sourceType);
    if (!found) {
      return 'unknown';
    }
    return found.cast;
  }

  private async prepareResource<ResultType extends string, SourceType extends string = ResultType>(
    key: 'app-audio' | 'page-audio' | 'page-media',
    sourceType: SourceType,
    sourceUrl: string | null | undefined,
  ): Promise<IResourcePrepared<ISourceResource<SourceType>> | null> {
    if (!sourceUrl) {
      return null;
    }
    const id = uuidV4();
    const bowser = Bowser.parse(window.navigator.userAgent);
    let resource: IMediaResource = {
      id,
      key,
      type: (this.castResourceType(sourceType) as ResultType),
      url: sourceUrl,
    } as IMediaResource;

    if (
      /\.(jpg|jpeg|png|gif|webp|svg|mov|mp4|avi|mkv)$/i.test(sourceUrl)
      && (bowser.os.name?.toLowerCase() === 'ios' || (bowser.os.name?.toLowerCase() === 'macos' && bowser.browser.name?.toLowerCase() === 'safari'))
    ) {
      resource.url = `/media/get-assets?data=${Base64.encodeURL(sourceUrl)}`;
    }

    if (this.resourceProcessor instanceof ResourceProcessor || this.resourceProcessor instanceof ResourceProcessorBatch) {
      resource = await this.resourceProcessor.call(resource);
    }

    const data: ISourceResource<SourceType> = {
      id,
      type: sourceType,
      url: resource.url,
    };
    return {
      data,
      resource,
    };
  }

  private async prepareCommon(source: IOriginData): Promise<IPagePrepared<ICommon>> {
    const [, type, param, code] = source.webUrl.replace(/https?:\/\//, '').split('/');

    const data: ICommon = {
      id: source?.id,
      type: source?.isB2b === true ? 'b2b' : 'b2c',
      uuid: source?.mobigramUuid,
      sender: source?.sender.name || '',
      identity: {
        type,
        param,
        code,
      },
      url: source?.webUrl,
      isShareableLink: source?.isShareableLink || false,
    };

    const resources: Array<IMediaResource> = [];

    const getAudioURL = (sourceUrl: string) => {
      if (!/\.amazonaws\.com/.test(sourceUrl)) {
        return sourceUrl;
      }
      const withBucketRegExp = /https:\/\/([A-z0-9-]+)\.([A-z0-9-.]+?)\.amazonaws\.com\/(.*)/;
      const withoutBucketRegExp = /https:\/\/([A-z0-9-]+)\.amazonaws\.com\/([A-z0-9-]+)\/(.*)/;
      let bucket;
      let region;
      let path;
      if (withoutBucketRegExp.test(sourceUrl)) {
        [, region, bucket, path] = sourceUrl.match(withoutBucketRegExp) || [];
      }
      if (withBucketRegExp.test(sourceUrl)) {
        [, bucket, region, path] = sourceUrl.match(withBucketRegExp) || [];
      }
      return `/media/${bucket}/${region}/audio.mp3?source=${path}`;
    };

    if (source.song && source.song?.source === 'feed_fm') {
      const { token = '', secret = '' } = source.song?.feedFmCredentials || {};
      const id = uuidV4();
      const url = `AdaptrFeedFm://${source.song.feedFmId}/${source.song.audioName}?token=${token}&secret=${secret}&requestParamName=mobigram_uuid&requestParamValue=${source.mobigramUuid}`;
      data.audio = {
        id,
        type: 'audio',
        url,
      };
      resources.push({
        id,
        key: 'app-audio',
        url,
        type: 'adaptrFeedFm',
      });
    }
    if (source.song && source.song?.source !== 'feed_fm') {
      const audioUrl = getAudioURL(source?.song?.audioUrl);
      const audio = await this.prepareResource<'audio'>('app-audio', 'audio', audioUrl);
      if (audio) {
        const { data: audioData, resource: audioResource } = audio;
        data.audio = audioData;
        resources.push(audioResource);
      }
    }

    return {
      data,
      resources,
    };
  }

  private async preparePageCover(source: IOriginData): Promise<IPagePrepared<IPageCover>> {
    const [
      stamp,
      logo,
      background,
    ] = await Promise.all([
      this.prepareResource<'image'>('page-media', 'image', 'https://cardsnacks-media-files.s3.amazonaws.com/CardsnacksLogoPlusStamp.png'),
      this.prepareResource<'image'>('page-media', 'image', source.envelope.logoUrl || ''),
      this.prepareResource<'image'>('page-media', 'image', source.envelope.backgroundImageUrl || ''),
    ]);

    const data: IPageCover = {
      id: uuidV4(),
      type: 'PageCover',
      items: {
        sender: source.sender.name,
        theme: {
          color1: source.envelope.themeColor1,
          color2: source.envelope.themeColor2,
        },
      },
      itemsOrder: ['theme', 'sender'],
    };

    const resources: Array<IMediaResource> = [];

    if (stamp) {
      const { data: stampData, resource: stampResource } = stamp;
      data.items.stamp = stampData;
      resources.push(stampResource);
      data.itemsOrder.push('stamp');
    }

    if (logo) {
      const { data: logoData, resource: logoResource } = logo;
      data.items.logo = logoData;
      resources.push(logoResource);
      data.itemsOrder.push('logo');
    }

    if (background) {
      const { data: backgroundData, resource: backgroundResource } = background;
      data.items.background = backgroundData;
      resources.push(backgroundResource);
      data.itemsOrder.push('background');
    }

    return {
      data,
      resources,
    };
  }

  private async preparePageContent(page: IOriginDataPageMessage): Promise<IPagePrepared<IPageContent>> {
    const data: IPageContent = {
      id: uuidV4(),
      type: 'PageContent',
      items: {},
      itemsOrder: [],
    };

    const resources: Array<IMediaResource> = [];

    const [visual, poster] = await Promise.all([
      this.prepareResource<'image' | 'video'>('page-media', page.resource.type, page.resource.url),
      page.resource.type === 'video' && page.resource.thumbnailUrl ? this.prepareResource<'poster'>('page-media', 'poster', page.resource.thumbnailUrl) : null,
    ]);

    if (visual) {
      const { data: dataMedia, resource: resourceMedia } = visual;
      data.items.visual = {
        loop: !page.resource.doesContainVoice,
        filter: 'none',
        media: dataMedia,
      };
      data.itemsOrder.push('visual');
      resources.push(resourceMedia);
    }
    if (data?.items?.visual && poster && poster.data.url && poster.data.url.length > 0) {
      const { data: dataPoster, resource: resourcePoster } = poster;
      data.items.visual.poster = dataPoster;
      resources.push(resourcePoster);
    }
    if (page.eventWidget) {
      data.items.eventWidget = {
        id: Number(page.eventWidget.id),
        title: page.eventWidget.title,
        locationName: page.eventWidget.locationName,
        locationAddress: page.eventWidget.locationAddress,
        startTime: page.eventWidget.startTime,
        endTime: page.eventWidget.endTime,
        shouldExcludeChildren: page.eventWidget.shouldExcludeChildren || false,
        position: {
          x: page.eventWidget.position.xOrigin,
          y: page.eventWidget.position.yOrigin,
          width: page.eventWidget.position.width,
          height: page.eventWidget.position.height,
        },
      };
    }
    if (page.buttonWidget) {
      data.items.buttonWidget = {
        text: page.buttonWidget.text,
        textColor: page.buttonWidget.textColor,
        backgroundColor: page.buttonWidget.backgroundColor,
        url: page.buttonWidget.url,
        position: {
          x: page.buttonWidget.position.xOrigin,
          y: page.buttonWidget.position.yOrigin,
          width: page.buttonWidget.position.width,
          height: page.buttonWidget.position.height,
        },
      };
    }
    if (page.bottomLabel) {
      data.items.bottomLabel = page.bottomLabel;
    }

    return {
      data,
      resources,
    };
  }

  private async preparePageGift(page: IOriginDataPageGift): Promise<IPagePrepared<IPageGift>> {
    const data: IPageGift = {
      id: uuidV4(),
      type: 'PageGift',
      items: {},
      itemsOrder: [],
    };

    const resources: Array<IMediaResource> = [];

    if (!page?.giftInfo) {
      return {
        data,
        resources,
      };
    }

    if (page?.giftInfo?.giftCard) {
      const image = await this.prepareResource<'image'>('page-media', 'image', page?.giftInfo?.giftCard?.imageUrl);
      const { data: imageData, resource: imageResource } = image || {};
      data.items.gift = {
        id: page?.giftInfo?.giftCard.id || null,
        type: 'virtual',
        isCardsnacksCash: page?.giftInfo?.giftCard?.isCardsnacksCash,
        source: page?.giftInfo?.giftCard?.source,
        platform: {
          name: page?.giftInfo?.giftCard?.name,
        },
        currency: {
          value: page?.giftInfo?.giftCard?.country?.currency,
          symbol: page?.giftInfo?.giftCard?.country?.currencySymbol,
        },
        amount: page?.giftInfo?.amount,
        link: page?.giftInfo?.shortlink,
        image: imageData || null,
        terms: page?.giftInfo?.giftCard?.terms,
        expiration: page?.giftInfo?.expirationDate,
      };
      data.itemsOrder.push('gift');
      if (imageResource) {
        resources.push(imageResource);
      }
    }

    if (page?.giftInfo?.physicalGift) {
      const { physicalGift, physicalGiftStatus: status, message = null } = page.giftInfo;
      const image = await this.prepareResource<'image'>('page-media', 'image', physicalGift.imageUrl || physicalGift.imageUrls?.[0]);
      const { data: imageData, resource: imageResource } = image || {};
      data.items.gift = {
        id: physicalGift.id,
        type: 'physical',
        status: status || 'closed',
        name: physicalGift.name,
        image: imageData || null,
        amount: physicalGift.cardsnacksPrice,
        brandName: physicalGift.brandName || null,
        title: physicalGift.subtitleShort || physicalGift.subtitleLong || null,
        description: physicalGift.recipientDescription || null,
        message,
        source: {
          id: physicalGift?.[`${physicalGift?.source}Id`],
          name: physicalGift?.source,
        },
        currency: {
          symbol: physicalGift.country.currencySymbol,
          value: physicalGift.country.currency,
        },
        link: null,
      };
      data.itemsOrder.push('gift');
      if (imageResource) {
        resources.push(imageResource);
      }
    }

    return {
      data,
      resources,
    };
  }

  private async preparePagePromo(page: IOriginDataPagePromo): Promise<IPagePrepared<IPagePromo>> {
    const data: IPagePromo = {
      id: uuidV4(),
      type: 'PagePromo',
      items: {},
      itemsOrder: [],
    };

    const resources: Array<IMediaResource> = [];

    const hasPromo = typeof page?.promoAdInfo !== 'undefined' && page?.promoAdInfo !== null;
    if (!hasPromo) {
      return {
        data,
        resources,
      };
    }

    const promo = page?.promoAdInfo;
    const image = await this.prepareResource<'image'>('page-media', 'image', hasPromo ? (page?.resource?.url || promo?.imageUrl) : null);
    if (hasPromo) {
      data.items.promo = {
        action: 'none',
        id: promo?.id ? Number(promo?.id) : undefined,
        title: promo?.title || '',
        forward: {
          url: promo?.buttonUrl || '',
        },
        pixel: {
          url: promo?.pixelUrl || '',
        },
        button: {
          text: promo?.buttonText || '',
          color: promo?.buttonColor || '',
          url: promo?.buttonUrl || '',
        },
        hasButton: true,
      };
      if (image) {
        const { data: imageData, resource: imageResource } = image;
        data.items.promo.image = imageData;
        resources.push(imageResource);
      }
      data.itemsOrder.push('promo');
    }
    return {
      data,
      resources,
    };
  }

  private async preparePageEnd(page: IOriginDataPageEnd): Promise<IPagePrepared<IPageEnd>> {
    const data: IPageEnd = {
      id: uuidV4(),
      type: 'PageEnd',
      items: {},
      itemsOrder: [],
    };

    const resources: Array<IMediaResource> = [];

    if (page?.info?.hasReply) {
      data.items.reply = {
        action: 'next',
        hasFrom: page?.info?.isShareableLink,
      };
      data.itemsOrder.push('reply');
    }

    if (page?.info?.hasBirthday) {
      data.items.birthday = {
        action: 'next',
      };
      data.itemsOrder.push('birthday');
    }

    if (page?.info?.hasHoliday) {
      data.items.holiday = {
        action: 'next',
      };
      data.itemsOrder.push('holiday');
    }

    if (page?.info?.hasPromoTSO) {
      data.items.promoTSO = {
        action: 'next',
      };
      data.itemsOrder.push('promoTSO');
    }

    if (page?.promo) {
      const promo = await this.preparePagePromo(page?.promo);
      if (promo.resources.length > 0 && promo.data.items.promo) {
        data.items.promo = {
          ...promo.data.items.promo,
          action: 'none',
          hasButton: false,
        };
        data.itemsOrder.push('promo');
        resources.push(...promo.resources);
        return {
          data,
          resources,
        };
      }
    } else {
      data.items.download = {
        action: 'none',
      };
      data.itemsOrder.push('download');
    }

    return {
      data,
      resources,
    };
  }
}

export default DataAdapter;
